import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { surveysByCreation } from '../../../containers/Page/MultiMarket/markets.utils'
import { FieldworkV2Query } from '../../../data/gql-gen/questionnaire/graphql'
import { GET_FIELDWORK } from '../../../data/gql/questionnaire/queries/fieldwork'
import { useScopedSurveys } from '../../../hooks/useScopedSurveys'
import {
  composeMarketName,
  countryLabel,
  flagEmoji,
  isCountryCodedMarketName,
  parseMarketName,
  splitMarketName
} from '../../../utils/marketFlags'

export interface MarketOption {
  surveyId: string
  label: string
  flag: string
  // 2-letter country code (uppercased): from the survey name for added markets,
  // from the fieldwork locale for the base market, else '' when unknown.
  country: string
  // The market's editable audience label ("United Kingdom — Boost (GB)" ->
  // "Boost"), '' when it has none. `label` keeps the country and label joined;
  // this is the part that tells markets in the same country apart.
  audienceLabel: string
}

const toMarketOption = (surveyId: string, name: string): MarketOption => ({
  surveyId,
  ...parseMarketName(name),
  audienceLabel: splitMarketName(name).audienceLabel
})

/**
 * Compact market name for narrow columns: the country code, joined to the
 * audience label when there is one ("US", "US — Boost"), so several markets in
 * the same country are tellable apart. Falls back to the full label when the
 * name carries no country code.
 */
export const shortMarketLabel = (market: MarketOption): string =>
  market.country
    ? composeMarketName({
        countryPrefix: market.country,
        // The code is already the prefix — it must not repeat as a suffix.
        countryCode: '',
        audienceLabel: market.audienceLabel
      })
    : market.label

/**
 * Display label + flag for the base (master) market. New multi-market projects
 * name their master like any other market ("United Kingdom — Boost (GB)"), so
 * its label comes from the name — audience label included. Masters created
 * before that (named after their project) have no country in their name, so
 * theirs comes from the fieldwork locale instead. Returns undefined until the
 * project (and thus the master survey) is known.
 *
 * Shared by every surface that lists the base market alongside the added ones
 * (project/audiences/fieldwork/confirm), so each renders it the same way.
 */
export const useBaseMarketLabel = (): MarketOption | undefined => {
  // The markets in scope, not a surveyId: this runs on ProjectPage / Audiences,
  // which have no :surveyId in the route. Inside a wave, the base is that wave's
  // oldest market, since each wave has its own questionnaire.
  const surveys = useScopedSurveys()

  const masterSurvey = surveysByCreation(surveys).at(0)
  const masterSurveyId = masterSurvey?.surveyId

  // Only a master still named after its project needs the locale — a market
  // name already carries its country. cache-first, so it's shared with the
  // other steps' fieldwork reads.
  const namedAsMarket = isCountryCodedMarketName(masterSurvey?.name)
  const { data: masterFieldwork } = useQuery<FieldworkV2Query>(GET_FIELDWORK, {
    context: { clientName: 'questionnaire' },
    variables: { surveyId: masterSurveyId },
    skip: !masterSurveyId || namedAsMarket
  })

  return useMemo(() => {
    if (!masterSurvey) {
      return undefined
    }

    if (namedAsMarket) {
      return toMarketOption(masterSurvey.surveyId, masterSurvey.name)
    }

    const country = (
      masterFieldwork?.fieldwork?.locale.country ?? ''
    ).toUpperCase()
    // Fall back to the survey name until the locale resolves, so the base market
    // is always present (its surveyId — what save/filter use — is correct either
    // way); only the label/flag refine once fieldwork loads.
    // Either way there is no audience label to parse: this master is named after
    // its project, so anything after a separator in its name is project text
    // (which is also why Market settings hides its audience label field).
    return country
      ? {
          surveyId: masterSurvey.surveyId,
          label: countryLabel(country),
          flag: flagEmoji(country),
          country,
          audienceLabel: ''
        }
      : {
          surveyId: masterSurvey.surveyId,
          ...parseMarketName(masterSurvey.name),
          audienceLabel: ''
        }
  }, [masterSurvey, masterFieldwork, namedAsMarket])
}

/**
 * The project's markets as display rows for the market-visibility / preview /
 * mapping surfaces, the base (master) market included.
 *
 * The market listed first is the auto-created master survey — the oldest, not
 * necessarily the one flagged isBase. It is a real market that respondents take,
 * so it is listed alongside the markets added later and its questions/options
 * can be hidden the same way — it is not special-cased out.
 * Added markets derive their label + flag from the survey name (the way the
 * market tabs do); the base market's come from useBaseMarketLabel. The base
 * market is listed first — it is the origin all markets derive from, and so the
 * natural default wherever a market must be picked (preview/mapping).
 */
export const useProjectMarkets = (): MarketOption[] => {
  const surveys = useScopedSurveys()
  const base = useBaseMarketLabel()

  return useMemo(() => {
    const added = surveysByCreation(surveys)
      // The master is the oldest; pull it out so it isn't double-listed, then
      // re-prepend its resolved label below.
      .filter((survey) => survey.surveyId !== base?.surveyId)
      .map((survey) => toMarketOption(survey.surveyId, survey.name))
      // Alphabetical so the dialog is scannable; the market tabs keep their
      // own (creation) order via getMarkets directly.
      .sort((a, b) => a.label.localeCompare(b.label))

    return base ? [base, ...added] : added
  }, [surveys, base])
}
